import { api } from "../api";
import { copyText, el, clear } from "../dom";
import { defFromEvent, mountEventForm } from "../eventform";
import { asStateWrap } from "../../shared/sync";
import { changedEventFields, conflictingFields, conflictNotice, logSentence, type EventFields } from "../../shared/present";
import type { EventState, LogEntry } from "../../shared/types";

export interface OrgHandle {
  update(state: EventState): void;
  showError(message: string): void;
  refreshLog(): void;
  destroy(): void;
}

function ensureButton(parent: HTMLElement, found: Element | null, make: () => HTMLButtonElement): HTMLButtonElement {
  if (found instanceof HTMLButtonElement) return found;
  const created = make();
  parent.append(created);
  return created;
}

function fingerprint(state: EventState): string {
  const e = state.event;
  return [
    e.title,
    e.note,
    e.timezone,
    e.dates.join(","),
    e.dayStartMin,
    e.dayEndMin,
    e.durationMin,
    JSON.stringify(e.polls),
    e.deadline ?? "",
    state.expected.join(","),
  ].join("|");
}

function asPatch(v: unknown): { state: EventState; dropped: { cells: number; votes: number; people: number } } | null {
  const wrap = asStateWrap(v);
  if (!wrap || typeof v !== "object" || v === null) return null;
  const rec = v as Record<string, unknown>;
  if (!rec.dropped || typeof rec.dropped !== "object") return { state: wrap.state, dropped: { cells: 0, votes: 0, people: 0 } };
  const d = rec.dropped as Record<string, unknown>;
  return {
    state: wrap.state,
    dropped: {
      cells: typeof d.cells === "number" ? d.cells : 0,
      votes: typeof d.votes === "number" ? d.votes : 0,
      people: typeof d.people === "number" ? d.people : 0,
    },
  };
}

function asLog(v: unknown): { log: LogEntry[] } | null {
  if (typeof v !== "object" || v === null || !("log" in v)) return null;
  const rec = v as Record<string, unknown>;
  if (!Array.isArray(rec.log)) return null;
  return { log: rec.log as LogEntry[] };
}

export function mountOrganizer(
  host: HTMLDetailsElement,
  opts: {
    eventId: string;
    getAdmin: () => string | null;
    onState: (state: EventState) => void;
    onNotice: (text: string) => void;
  },
): OrgHandle {
  let lastFp = "";
  let lastState: EventState | null = null;
  let baseline: EventFields = defFromEvent(
    {
      id: "",
      title: "",
      note: "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      dates: [],
      dayStartMin: 9 * 60,
      dayEndMin: 17 * 60,
      slotMin: 30,
      durationMin: 60,
      polls: [],
      deadline: null,
      createdAt: "",
    },
    [],
  );
  let shownSeq = 0;
  let logReq = 0;

  const share = el("p", { class: "muted", text: "Keep this one private. Anyone who has it can edit, lock and remove people." });
  const adminUrl = el("input", { type: "text", readOnly: true });
  const copyAdmin = el("button", { type: "button", text: "Copy organizer link", "data-testid": "org-copy-admin" });
  copyAdmin.addEventListener("click", () => {
    void (async () => {
      const result = await copyText(adminUrl.value, adminUrl);
      copyAdmin.textContent = result === "copied" ? "Copied" : "Press Ctrl+C to copy";
      setTimeout(() => {
        copyAdmin.textContent = "Copy organizer link";
      }, 2000);
    })();
  });
  const form = mountEventForm({
    titleTestId: "org-title",
    includeExpected: true,
  });
  const notice = el("p", { class: "muted", "data-testid": "org-notice" });
  const err = el("p", { class: "error", "data-testid": "org-error" });
  const save = el("button", { class: "primary", type: "button", text: "Save changes", "data-testid": "org-save" });
  const peopleBox = el("div");
  const activity = el("ul", { class: "plain", "data-testid": "org-activity" });
  const refresh = el("button", { type: "button", text: "Refresh", "data-testid": "log-refresh" });
  refresh.addEventListener("click", () => {
    void loadLog();
  });

  async function busy(btn: HTMLButtonElement, fn: () => Promise<void>): Promise<void> {
    if (btn.disabled) return;
    btn.disabled = true;
    try {
      await fn();
    } catch (e) {
      console.error(e);
      err.textContent = "Something went wrong. Try again.";
    } finally {
      btn.disabled = false;
    }
  }

  // The whole form is locked while a save runs (the dry run, the confirmation, the real PATCH). The
  // success path fills every control from the server's answer, so anything typed in between would
  // be thrown away; a disabled fieldset makes that impossible instead of merely unlikely.
  const formLock = el("fieldset", { class: "form-lock" }, form.root);

  save.addEventListener("click", () => {
    void busy(save, async () => {
      err.textContent = "";
      const token = opts.getAdmin();
      if (!token) return;
      formLock.disabled = true;
      try {
        await saveForm(token);
      } finally {
        formLock.disabled = false;
      }
    });
  });

  async function saveForm(token: string): Promise<void> {
    const current = form.read();
    if ("error" in current) {
      err.textContent = current.error;
      return;
    }
    // Only what this organizer actually touched is sent: a field they never opened stays out of
    // the payload entirely, so the server keeps whatever anyone else did to it.
    const payload = changedEventFields(baseline, current);
    if (Object.keys(payload).length === 0) {
      notice.textContent = "Nothing to change";
      return;
    }
    const dry = await api("PATCH", `/api/events/${opts.eventId}?dryRun=1`, {
      body: payload,
      adminToken: token,
      parse: asPatch,
    });
    if (!dry.ok) {
      err.textContent = dry.error;
      return;
    }
    const dropped = dry.data.dropped;
    if (dropped.cells + dropped.votes > 0) {
      const n = dropped.cells + dropped.votes;
      if (!confirm(`This change removes ${n} answers from ${dropped.people} people. Continue?`)) return;
    }
    const expectedRev = dry.data.state.rev;
    const real = await api("PATCH", `/api/events/${opts.eventId}`, {
      body: { ...payload, expectedRev },
      adminToken: token,
      parse: asPatch,
    });
    if (!real.ok) {
      err.textContent = real.error;
      return;
    }
    const realDropped = real.data.dropped;
    if (realDropped.cells !== dropped.cells || realDropped.votes !== dropped.votes || realDropped.people !== dropped.people) {
      notice.textContent = `This save removed ${realDropped.cells + realDropped.votes} answers from ${realDropped.people} people (the preview was ${dropped.cells + dropped.votes} from ${dropped.people}).`;
      opts.onNotice(notice.textContent);
    } else {
      notice.textContent = "";
    }
    baseline = defFromEvent(real.data.state.event, real.data.state.expected);
    form.set(baseline);
    lastFp = fingerprint(real.data.state);
    lastState = real.data.state;
    opts.onState(real.data.state);
    void loadLog();
  }

  const box = el("div", { class: "org-form" });
  box.append(
    share,
    adminUrl,
    copyAdmin,
    formLock,
    notice,
    err,
    save,
    el("h3", { text: "People" }),
    peopleBox,
    el("h3", { text: "Activity" }),
    refresh,
    activity,
  );
  const summary = el("summary", { class: "label", text: "Organizer tools" });
  host.replaceChildren(summary, box);

  host.addEventListener("toggle", () => {
    if (host.open) void loadLog();
  });

  function renderPeople(state: EventState): void {
    const token = opts.getAdmin();
    const keep = new Set(state.participants.map((p) => p.id));
    for (const node of [...peopleBox.children]) {
      if (!(node instanceof HTMLElement)) continue;
      const pid = node.dataset.pid;
      if (pid && !keep.has(pid)) node.remove();
    }
    for (const p of state.participants) {
      const rowFound = peopleBox.querySelector(`[data-pid="${CSS.escape(p.id)}"]`);
      let row: HTMLElement;
      if (rowFound instanceof HTMLElement) {
        row = rowFound;
      } else {
        row = el("div", { class: "person-admin", "data-pid": p.id });
        peopleBox.append(row);
      }
      const nameFound = row.querySelector("strong");
      let name: HTMLElement;
      if (nameFound instanceof HTMLElement) {
        name = nameFound;
      } else {
        clear(row);
        name = el("strong");
        row.append(name);
      }
      name.textContent = p.name;
      const flagFound = row.querySelector(".req-flag");
      let flag: HTMLElement;
      if (flagFound instanceof HTMLElement) {
        flag = flagFound;
      } else {
        flag = el("span", { class: "muted req-flag" });
        row.append(flag);
      }
      flag.textContent = p.required ? " required" : "";
      const reqBtn = ensureButton(row, row.querySelector('[data-testid="required-toggle"]'), () =>
        el("button", { type: "button", "data-testid": "required-toggle", "data-pid": p.id }),
      );
      reqBtn.textContent = p.required ? "Unmark required" : "Mark required";
      reqBtn.onclick = () => {
        void busy(reqBtn, async () => {
          err.textContent = "";
          if (!token) return;
          const res = await api("PATCH", `/api/events/${opts.eventId}/participants/${p.id}`, {
            body: { required: !p.required },
            adminToken: token,
            parse: asStateWrap,
          });
          if (!res.ok) {
            err.textContent = res.error;
            return;
          }
          opts.onState(res.data.state);
          void loadLog();
        });
      };
      const rmBtn = ensureButton(row, row.querySelector('[data-testid="remove-person"]'), () =>
        el("button", { type: "button", text: "Remove", "data-testid": "remove-person", "data-pid": p.id }),
      );
      rmBtn.onclick = () => {
        void busy(rmBtn, async () => {
          err.textContent = "";
          if (!confirm(`Remove ${p.name} and their answers? This cannot be undone.`)) return;
          if (!token) return;
          const res = await api("DELETE", `/api/events/${opts.eventId}/participants/${p.id}`, {
            adminToken: token,
            parse: asStateWrap,
          });
          if (!res.ok) {
            err.textContent = res.error;
            return;
          }
          opts.onState(res.data.state);
          void loadLog();
        });
      };
    }
  }

  async function loadLog(): Promise<void> {
    const token = opts.getAdmin();
    const st = lastState;
    if (!token || !st) return;
    const req = ++logReq;
    const res = await api("GET", `/api/events/${opts.eventId}/log`, { adminToken: token, parse: asLog });
    if (req !== logReq) return;
    if (!res.ok) {
      err.textContent = res.error;
      return;
    }
    const newest = res.data.log.slice(-20).reverse();
    const last = newest[0]?.seq ?? 0;
    if (last < shownSeq) return;
    shownSeq = last;
    clear(activity);
    for (const entry of newest) {
      const ignored = entry.kind === "ignored" ? Number(entry.detail.ignored) : 0;
      const text =
        entry.kind === "ignored"
          ? `${String(entry.detail.name ?? entry.actor)}'s save dropped ${ignored} answers on times that were removed`
          : logSentence(entry, st.event.timezone);
      activity.append(el("li", { text }));
    }
  }

  return {
    update(state: EventState) {
      const fp = fingerprint(state);
      const firstLoad = lastState === null;
      lastState = state;
      adminUrl.value = `${location.origin}/e/${state.event.id}#admin=${opts.getAdmin() ?? ""}`;
      if (!firstLoad && form.isDirty()) {
        // A live update while the form is dirty leaves the form and the baseline alone.
        if (lastFp !== fp) {
          const current = form.read();
          const fields = "error" in current
            ? []
            : conflictingFields(baseline, defFromEvent(state.event, state.expected), current);
          notice.textContent =
            fields.length > 0
              ? conflictNotice(fields)
              : "This poll changed while you were editing. Your unsaved edits are still here.";
        }
      } else {
        baseline = defFromEvent(state.event, state.expected);
        form.set(baseline);
        lastFp = fp;
        notice.textContent = notice.textContent.startsWith("This save removed") ? notice.textContent : "";
      }
      renderPeople(state);
    },
    showError(message: string) {
      err.textContent = message;
    },
    refreshLog() {
      void loadLog().catch((e) => {
        console.error(e);
        err.textContent = "Something went wrong. Try again.";
      });
    },
    destroy() {
      lastState = null;
      host.replaceChildren();
    },
  };
}
